import { Button, TableCell } from "@rothoblaas/design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import Table from "../../../../../components/ui/Table";
import useListFilters from "../../../../../hooks/useListFilters";
import { useAuthContext } from "../../../../../context/authContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useLockCalculatorReportApi } from "../../../../../http/lock_calculator/useReport";
import {
  type Configuration,
  type LockCalculatorDefinition,
} from "../../../../../http/lock_calculator/types";
import { connectorCode } from "../../model/connectorCode";
import { hasRealLoadValue } from "../../model/structuralVisibility";
import { kSerLabel } from "./kSer";
import styles from "../../LockCalculator.module.css";

type Props = {
  calculation: LockCalculatorDefinition | null;
  onSelect: (configurationId: string) => void;
  onCompare: (selected: Configuration[]) => void;
};

const MAX_COMPARISON_SELECTION = 5;

const axisEta = (load: number | null | undefined, resistance: number | null | undefined): number | null => {
  if (load == null || load <= 0 || resistance == null || resistance <= 0) return null;
  return (load * 1000) / resistance;
};

type AxisKey = "vertical" | "uplift" | "lateral" | "axial";

type AxisDef = {
  key: AxisKey;
  load: (calculation: LockCalculatorDefinition | null) => number | null | undefined;
  resistance: (result: Configuration["result"]) => number | null | undefined;
};

/** Order also drives column order in the table: lateral before axial. */
const AXES: AxisDef[] = [
  { key: "vertical", load: (c) => c?.verticalLoad, resistance: (r) => r?.fvD },
  { key: "uplift", load: (c) => c?.upliftLoad, resistance: (r) => r?.fupD },
  { key: "lateral", load: (c) => c?.lateralLoad, resistance: (r) => r?.flatD },
  { key: "axial", load: (c) => c?.axialLoad, resistance: (r) => r?.faxD },
];


/** Joins every connector's lock code — a draft may have one lock or, since
 * automatic generation can now pair a compatible second lock alongside
 * the first, two. */
const connectorsLabel = (row: Configuration): string => {
  const codes = (row.connectors ?? []).map(connectorCode).filter((code): code is string => !!code);
  return codes.length > 0 ? codes.join(" + ") : "—";
};

/** Whether at least two Include* flags are selected — mirrors the backend's
 * computeCombined threshold (calculation_verification.go): below that, no
 * combined result exists at all, so the column has nothing meaningful to show. */
const hasCombinedVerification = (calculation: LockCalculatorDefinition | null): boolean => {
  const flags = [calculation?.includeFv, calculation?.includeFup, calculation?.includeFax, calculation?.includeFlat];
  return flags.filter(Boolean).length >= 2;
};

/** Mirrors buildCandidates' useInclined gating (configuration_generator_service.go)
 * — a resistance config picked for an axis with no real load behind it (0,
 * null, or undefined) is stale, same rule as hasRealLoadValue everywhere else. */
const hasInclinedFastener = (calculation: LockCalculatorDefinition | null): boolean =>
  (calculation?.lateralResistanceConfig === 3 && hasRealLoadValue(calculation?.lateralLoad)) ||
  (calculation?.upliftResistanceConfig === 0 && hasRealLoadValue(calculation?.upliftLoad));

/** Mirrors buildCandidates' useLockstop gating (configuration_generator_service.go). */
const hasLockstop = (calculation: LockCalculatorDefinition | null): boolean =>
  calculation?.lateralResistanceConfig === 4 && hasRealLoadValue(calculation?.lateralLoad);

/** Mirrors computeVerticalDown's K_ser gate (eta_190831_lock.go) — the slip
 * modulus is only computed server-side with a real (positive) lateral load. */
const hasKSer = (calculation: LockCalculatorDefinition | null): boolean =>
  (calculation?.lateralLoad ?? 0) > 0;

/** Testo di una cella eta e di una cella K_ser. Sono usate sia dalle celle sia
 * dall'indice di ricerca: la ricerca vede esattamente cio' che e' scritto. */
const etaLabel = (eta: number | null | undefined): string => (eta == null ? "—" : `${(eta * 100).toFixed(2)}%`);


/** File name from the entered data: standard, header/joist grade and section, count, date. */
const exportFilename = (calculation: LockCalculatorDefinition | null, count: number): string => {
  const section = (grade?: { code: string } | null, b?: number | null, h?: number | null) =>
    [grade?.code, b != null && h != null ? `${b}x${h}` : null].filter(Boolean).join("-");
  const parts = [
    "lock",
    calculation?.standard?.code,
    `header_${section(calculation?.headerMaterialGrade, calculation?.headerB, calculation?.headerH)}`,
    `joist_${section(calculation?.joistMaterialGrade, calculation?.joistB, calculation?.joistH)}`,
    `${count}_configs`,
    new Date().toISOString().slice(0, 10),
  ];
  return `${parts.filter((part) => part && !part.endsWith("_")).join("_").replace(/[^\w.-]+/g, "-")}.csv`;
};

/** Renders server-generated candidate configurations; selecting a row promotes it to 'chosen'. */
export const GeneratedConfigurationsTable = ({ calculation, onSelect, onCompare }: Props) => {
  const { translation: t } = useI18n();
  const auth = useAuthContext();
  const { downloadConfigurationsCsv } = useLockCalculatorReportApi();
  const [isExporting, setIsExporting] = useState(false);
  const allRows = calculation?.draftConfigurations ?? [];
  const showCombined = hasCombinedVerification(calculation);
  const showInclined = hasInclinedFastener(calculation);
  const showLockstop = hasLockstop(calculation);
  const showKSer = hasKSer(calculation);
  const visibleAxes = AXES.filter((axis) => hasRealLoadValue(axis.load(calculation)));

  // Filtro su TUTTE le colonne: l'indice di una riga e' la concatenazione delle
  // sue celle, costruita dalle stesse espressioni che le celle renderizzano e
  // limitata alle colonne davvero visibili — cercare in una colonna nascosta
  // darebbe risultati che a schermo non si spiegano.
  const filters = useListFilters({ isSearchable: true, prefix: "lock_gen" });
  const query = (filters.state.q ?? "").trim().toLowerCase();
  const rows = useMemo(() => {
    if (!query) return allRows;
    const rowText = (row: Configuration) =>
      [
        connectorsLabel(row),
        row.headerFastenerItem?.item.code ?? "",
        row.joistFastenerItem?.item.code ?? "",
        showInclined ? row.inclinedFastenerItem?.item.code ?? "" : "",
        showLockstop ? row.lockstopItem?.item.code ?? "" : "",
        showCombined ? etaLabel(row.result?.etaCombined) : "",
        ...visibleAxes.map((axis) => etaLabel(axisEta(axis.load(calculation), axis.resistance(row.result)))),
        showKSer ? kSerLabel(row.result?.k23Ser) : "",
      ]
        .join(" ")
        .toLowerCase();
    return allRows.filter((row) => rowText(row).includes(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, query, calculation, showCombined, showInclined, showLockstop, showKSer, visibleAxes]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Drop selections whose row is no longer among the drafts (e.g. it was just
  // promoted to 'chosen'). Si guarda allRows, non rows: una riga nascosta dal
  // filtro esiste ancora, e deselezionarla mentre si scrive sarebbe un furto.
  useEffect(() => {
    const validIds = new Set(allRows.map((row) => row.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [allRows]);

  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const atMaxSelection = selectedIds.size >= MAX_COMPARISON_SELECTION;

  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleRow = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked && prev.size >= MAX_COMPARISON_SELECTION) return prev;
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(rows.slice(0, MAX_COMPARISON_SELECTION).map((row) => row.id)) : new Set());
  };

  // Il CSV e' costruito dal backend con TUTTE le bozze: la schermata ne riceve
  // un numero limitato (maxDraftConfigurationsReturned), l'export no.
  const exportCsv = async () => {
    if (!calculation?.calculationId) return;
    setIsExporting(true);
    try {
      await downloadConfigurationsCsv(
        calculation.calculationId,
        auth.identity?.language ?? "en",
        exportFilename(calculation, calculation.draftConfigurationsTotal ?? allRows.length),
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Nessuna configurazione generata e nessun filtro: e' l'altro messaggio, non
  // un "nessun risultato" — la tabella non ha proprio dati da mostrare.
  if (allRows.length === 0) {
    return <p className={styles.emptyConfigurationsMessage}>{t.lockCalculatorModule.generatedConfigurations.empty}</p>;
  }

  return (
    <div className={styles.tabTableWrapper}>
      <Table<Configuration>
        items={rows}
        filters={filters}
        search={{
          placeholder: t.lockCalculatorModule.generatedConfigurations.search,
          onChange: () => undefined,
        }}
        headingActions={
          <>
            <Button variant="secondary" disabled={isExporting} onClick={exportCsv}>
              {t.lockCalculatorModule.generatedConfigurations.downloadData}
            </Button>
            <Button variant="secondary" disabled={selectedIds.size < 1} onClick={() => onCompare(allRows.filter((row) => selectedIds.has(row.id)))}>
              {t.lockCalculatorModule.generatedConfigurations.compare}
            </Button>
          </>
        }
        head={
          <tr>
            <TableCell className={styles.checkboxTd}>
              <input
                ref={headerCheckboxRef}
                type="checkbox"
                checked={allSelected}
                aria-label={t.lockCalculatorModule.generatedConfigurations.selectAll}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => toggleAll(event.target.checked)}
              />
            </TableCell>
            <TableCell>{t.lockCalculatorModule.generatedConfigurations.lock}</TableCell>
            <TableCell>{t.lockCalculatorModule.fasteners.headerFastener}</TableCell>
            <TableCell>{t.lockCalculatorModule.fasteners.joistFastener}</TableCell>
            {showInclined && <TableCell>{t.lockCalculatorModule.structural.inclinedFastener}</TableCell>}
            {showLockstop && <TableCell>{t.lockCalculatorModule.fasteners.lockstop}</TableCell>}
            {showCombined && <TableCell>{t.lockCalculatorModule.generatedConfigurations.combined}</TableCell>}
            {visibleAxes.map((axis) => (
              <TableCell key={axis.key}>{t.lockCalculatorModule.results.axes[axis.key]}</TableCell>
            ))}
            {showKSer && <TableCell>{t.lockCalculatorModule.results.slipModulus}</TableCell>}
            <TableCell></TableCell>
          </tr>
        }
        renderRow={(row) => {
          return (
            <>
              <TableCell className={styles.checkboxTd}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(row.id)}
                  disabled={atMaxSelection && !selectedIds.has(row.id)}
                  aria-label={t.lockCalculatorModule.generatedConfigurations.selectRow}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => toggleRow(row.id, event.target.checked)}
                />
              </TableCell>
              <TableCell>{connectorsLabel(row)}</TableCell>
              <TableCell>{row.headerFastenerItem?.item.code || "—"}</TableCell>
              <TableCell>{row.joistFastenerItem?.item.code || "—"}</TableCell>
              {showInclined && <TableCell>{row.inclinedFastenerItem?.item.code || "—"}</TableCell>}
              {showLockstop && <TableCell>{row.lockstopItem?.item.code || "—"}</TableCell>}
              {showCombined && <EtaTd eta={row.result?.etaCombined} verified={row.result?.isVerified} />}
              {visibleAxes.map((axis) => {
                const eta = axisEta(axis.load(calculation), axis.resistance(row.result));
                return <EtaTd key={axis.key} eta={eta} verified={eta != null ? eta <= 1 : null} />;
              })}
              {showKSer && <TableCell>{kSerLabel(row.result?.k23Ser)}</TableCell>}
              <TableCell>
                <Button variant="secondary" onClick={() => onSelect(row.id)}>
                  {t.lockCalculatorModule.generatedConfigurations.load}
                </Button>
              </TableCell>
            </>
          );
        }}
      />

      {rows.length === 0 && <p className={styles.emptyConfigurationsMessage}>{t.common.noItemsFound}</p>}
    </div>
  );
};

const EtaTd = ({ eta, verified }: { eta: number | null | undefined; verified: boolean | null | undefined }) => {
  if (eta == null) return <TableCell>—</TableCell>;
  const statusClass = verified ? styles.statusOk : styles.statusFailed;
  return (
    <TableCell>
      <span className={`${styles.statusBadge} ${statusClass}`}>{etaLabel(eta)}</span>
    </TableCell>
  );
};
